import { useState } from 'react'
import { User, Search, FileText, Users, DollarSign, Clock, ChevronLeft, ChevronRight, Calendar } from 'lucide-react'
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ReferenceLine } from 'recharts';
import { format, startOfDay, endOfDay, eachDayOfInterval, isWithinInterval, addMinutes, addHours, parseISO, differenceInMinutes, differenceInHours } from 'date-fns';

export default function BillingDetails({ users }) {
    const [searchUid, setSearchUid] = useState('');
    const [billingData, setBillingData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [customPageInput, setCustomPageInput] = useState('');
    const [startDate, setStartDate] = useState(format(new Date().setDate(new Date().getDate() - 7), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [timeGranularity, setTimeGranularity] = useState('hour'); // 'minute', 'hour', 'day'
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'success', 'error'

    const preferredUsers = [
        { id: 'cetWAxqthaMbpjvhEqUN6OY8WV03', label: 'justin' },
        { id: '50fLEA9056Xhq7gjC6oVjVdFCrw1', label: 'incidents' },
        { id: 'WgVLxXREk7eW2aQeFOSfRbuxJbm1', label: 'barzoiu' }
    ];

    const fetchBillingData = async (userId) => {
        if (!userId.trim()) {
            toast.error('Please enter a user ID');
            return;
        }

        setLoading(true);
        setHasSearched(true);
        setBillingData([]); // Reset cards immediately

        try {
            const apiUrl = "https://api.extracta.ai/admin/getBilling";
            // const apiUrl = "http://localhost:3000/admin/getBilling";
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uid: userId
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch billing data');
            }

            const data = await response.json();

            // Transform the billing data into an array for table display
            const billingArray = Object.entries(data.billingData || {}).map(([id, billing]) => {
                // timestamp will always be present
                const timestamp = typeof billing.timestamp === 'number' && !isNaN(billing.timestamp) && billing.timestamp > 0
                    ? billing.timestamp
                    : null;

                // Check if startTime is valid
                let startTime = null;
                if (typeof billing.startTime === 'number' && !isNaN(billing.startTime) && billing.startTime > 0) {
                    startTime = billing.startTime;
                }

                // Only calculate processing time if both timestamp and startTime are valid
                let processingTime = null;
                if (timestamp && startTime) {
                    processingTime = Math.round((timestamp - startTime) / 1000);
                }

                return {
                    id,
                    origin: billing.origin || 'Unknown',
                    pagesNo: billing.pagesNo || 0,
                    startTime,
                    timestamp,
                    processingTime,
                    isError: false
                };
            });

            // Transform error data
            const errorArray = Object.entries(data.errorData || {}).map(([id, error]) => {
                const timestamp = typeof error.timestamp === 'number' && !isNaN(error.timestamp) && error.timestamp > 0
                    ? error.timestamp
                    : null;

                return {
                    id,
                    origin: 'Error',
                    pagesNo: 0,
                    startTime: null,
                    timestamp,
                    processingTime: null,
                    isError: true
                };
            });

            // Combine and sort all data by timestamp descending
            const allData = [...billingArray, ...errorArray].sort((a, b) => b.timestamp - a.timestamp);

            setBillingData(allData);
            setCurrentPage(1); // Reset to first page when new data is loaded
            toast.success(`Found ${billingArray.length} billing records and ${errorArray.length} errors`);
        } catch (error) {
            console.error('Error fetching billing data:', error);
            toast.error('Failed to fetch billing data');
            setBillingData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUserSearch = async () => {
        await fetchBillingData(searchUid);
    };

    const handlePreferredUserClick = async (userId) => {
        setSearchUid(userId);
        await fetchBillingData(userId);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleUserSearch();
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const calculateAverageTime = (timeFilter) => {
        if (billingData.length === 0) return 0;

        const now = Date.now();
        let filteredData = [];

        switch (timeFilter) {
            case '5min':
                filteredData = billingData.filter(record =>
                    record.timestamp && record.processingTime !== null &&
                    now - record.timestamp <= 5 * 60 * 1000
                );
                break;
            case '30min':
                filteredData = billingData.filter(record =>
                    record.timestamp && record.processingTime !== null &&
                    now - record.timestamp <= 30 * 60 * 1000
                );
                break;
            case 'today':
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                filteredData = billingData.filter(record =>
                    record.timestamp && record.processingTime !== null &&
                    record.timestamp >= todayStart.getTime()
                );
                break;
            case 'month':
                const monthStart = new Date();
                monthStart.setDate(1);
                monthStart.setHours(0, 0, 0, 0);
                filteredData = billingData.filter(record =>
                    record.timestamp && record.processingTime !== null &&
                    record.timestamp >= monthStart.getTime()
                );
                break;
            case 'last20':
                // Get last 20 successful records (non-error)
                filteredData = billingData
                    .filter(record => !record.isError && record.processingTime !== null)
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, 20);
                break;
            case 'all':
            default:
                filteredData = billingData.filter(record => record.processingTime !== null);
                break;
        }

        if (filteredData.length === 0) return 0;

        const totalTime = filteredData.reduce((sum, record) => sum + record.processingTime, 0);
        return Math.round(totalTime / filteredData.length);
    };

    // Filter data based on status
    const filteredData = billingData.filter(record => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'success') return !record.isError;
        if (statusFilter === 'error') return record.isError;
        return true;
    });

    // Update pagination logic to use filtered data
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = filteredData.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setCustomPageInput('');
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page
        setCustomPageInput('');
    };

    const handleCustomPageSubmit = () => {
        const pageNumber = parseInt(customPageInput);
        if (pageNumber && pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            setCustomPageInput('');
        } else {
            toast.error(`Please enter a valid page number between 1 and ${totalPages}`);
        }
    };

    const handleCustomPageKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleCustomPageSubmit();
        }
    };

    const statsCards = [
        {
            title: 'Last 20 Files',
            value: `${calculateAverageTime('last20')}s`,
            description: 'Average processing time',
            color: 'purple',
            count: billingData
                .filter(record => !record.isError && record.processingTime !== null)
                .sort((a, b) => b.timestamp - a.timestamp)
                .slice(0, 20).length
        },
        {
            title: 'Last 5 Minutes',
            value: `${calculateAverageTime('5min')}s`,
            description: 'Average processing time',
            color: 'blue',
            count: billingData.filter(record =>
                record.timestamp && record.processingTime !== null &&
                Date.now() - record.timestamp <= 5 * 60 * 1000
            ).length
        },
        {
            title: 'Last 30 Minutes',
            value: `${calculateAverageTime('30min')}s`,
            description: 'Average processing time',
            color: 'green',
            count: billingData.filter(record =>
                record.timestamp && record.processingTime !== null &&
                Date.now() - record.timestamp <= 30 * 60 * 1000
            ).length
        },
        {
            title: 'Today',
            value: `${calculateAverageTime('today')}s`,
            description: 'Average processing time',
            color: 'yellow',
            count: billingData.filter(record => {
                const todayStart = new Date();
                todayStart.setHours(0, 0, 0, 0);
                return record.timestamp && record.processingTime !== null &&
                    record.timestamp >= todayStart.getTime();
            }).length
        },
        {
            title: 'This Month',
            value: `${calculateAverageTime('month')}s`,
            description: 'Average processing time',
            color: 'indigo',
            count: billingData.filter(record => {
                const monthStart = new Date();
                monthStart.setDate(1);
                monthStart.setHours(0, 0, 0, 0);
                return record.timestamp && record.processingTime !== null &&
                    record.timestamp >= monthStart.getTime();
            }).length
        },
        {
            title: 'All Time',
            value: `${calculateAverageTime('all')}s`,
            description: 'Average processing time',
            color: 'pink',
            count: billingData.filter(record => record.processingTime !== null).length
        }
    ];

    const prepareGraphData = () => {
        if (!billingData.length) return [];

        const start = startOfDay(new Date(startDate));
        const end = endOfDay(new Date(endDate));

        let data = [];
        const timeDiff = differenceInMinutes(end, start);

        // Determine appropriate granularity based on time range
        if (timeDiff <= 1440) { // 24 hours or less
            // Create minute intervals
            let currentTime = start;
            while (currentTime <= end) {
                data.push({
                    timestamp: currentTime.getTime(),
                    time: format(currentTime, 'HH:mm'),
                    date: format(currentTime, 'MMM dd'),
                    count: 0,
                    errorCount: 0
                });
                currentTime = addMinutes(currentTime, 1);
            }
        } else if (timeDiff <= 10080) { // 7 days or less
            // Create hour intervals
            let currentTime = start;
            while (currentTime <= end) {
                data.push({
                    timestamp: currentTime.getTime(),
                    time: format(currentTime, 'HH:00'),
                    date: format(currentTime, 'MMM dd'),
                    count: 0,
                    errorCount: 0
                });
                currentTime = addHours(currentTime, 1);
            }
        } else {
            // Create day intervals
            const dateRange = eachDayOfInterval({ start, end });
            data = dateRange.map(date => ({
                timestamp: date.getTime(),
                time: format(date, 'MMM dd'),
                date: format(date, 'MMM dd'),
                count: 0,
                errorCount: 0
            }));
        }

        // Count requests and errors for each interval
        billingData.forEach(record => {
            if (record.timestamp && isWithinInterval(new Date(record.timestamp), { start, end })) {
                const intervalIndex = data.findIndex(d => {
                    const recordTime = new Date(record.timestamp);
                    if (timeDiff <= 1440) {
                        return format(recordTime, 'yyyy-MM-dd HH:mm') === format(new Date(d.timestamp), 'yyyy-MM-dd HH:mm');
                    } else if (timeDiff <= 10080) {
                        return format(recordTime, 'yyyy-MM-dd HH:00') === format(new Date(d.timestamp), 'yyyy-MM-dd HH:00');
                    } else {
                        return format(recordTime, 'yyyy-MM-dd') === format(new Date(d.timestamp), 'yyyy-MM-dd');
                    }
                });
                if (intervalIndex !== -1) {
                    if (record.isError) {
                        data[intervalIndex].errorCount++;
                    } else {
                        data[intervalIndex].count++;
                    }
                }
            }
        });

        return data;
    };

    const handleDateChange = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
        } else {
            setEndDate(value);
        }
    };

    const handleGranularityChange = (granularity) => {
        setTimeGranularity(granularity);
    };

    const getOutlierThreshold = (values) => {
        if (!values.length) return 0;

        const sortedValues = [...values].sort((a, b) => a - b);
        const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)];
        const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)];
        const iqr = q3 - q1;

        return Math.round(q3 + 1.5 * iqr);
    };

    const prepareLast24HoursProcessingTimeData = () => {
        const now = new Date();
        const last24hStart = now.getTime() - (24 * 60 * 60 * 1000);
        const processedFiles = billingData
            .filter((record) =>
                !record.isError &&
                record.processingTime !== null &&
                record.timestamp &&
                record.timestamp >= last24hStart &&
                record.timestamp <= now.getTime()
            )
            .sort((a, b) => a.timestamp - b.timestamp);

        const threshold = getOutlierThreshold(processedFiles.map((record) => record.processingTime));

        return {
            threshold,
            points: processedFiles.map((record) => ({
                timestamp: record.timestamp,
                processingTime: record.processingTime,
                pagesNo: record.pagesNo || 0,
                fullLabel: format(new Date(record.timestamp), 'MMM dd, HH:mm:ss'),
                fileId: record.id,
                shortId: String(record.id).slice(0, 8),
                isOutlier: threshold > 0 ? record.processingTime > threshold : false,
            }))
        };
    };

    const last24HoursProcessingData = prepareLast24HoursProcessingTimeData();

    return (
        <div className="space-y-8">
            {/* User Search Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-4">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">User Billing Lookup</h2>
                <p className="text-gray-600 mb-4">Search for a user by ID to view their billing data and processing history.</p>

                <div className="flex items-center space-x-4">
                    <div className="flex-1">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <User className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={searchUid}
                                onChange={(e) => setSearchUid(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter user ID..."
                                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    <button
                        onClick={handleUserSearch}
                        disabled={loading}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Search className="h-4 w-4 mr-2" />
                        {loading ? 'Searching...' : 'Search Billing Data'}
                    </button>
                </div>

                {/* Preferred Users */}
                <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">Quick access to preferred users:</p>
                    <div className="flex flex-wrap gap-2">
                        {preferredUsers.map((user, index) => (
                            <button
                                key={user.id}
                                onClick={() => handlePreferredUserClick(user.id)}
                                disabled={loading}
                                className="inline-flex items-center px-3 py-1.5 border border-indigo-200 text-xs font-medium rounded-full text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Users className="h-3 w-3 mr-1" />
                                {user.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* User Data Indicator */}
            {hasSearched && billingData.length > 0 && searchUid && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center">
                        <span className="text-sm text-gray-500">Showing data for user:</span>
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            <User className="h-3 w-3 mr-1" />
                            {preferredUsers.find(user => user.id === searchUid)?.label || searchUid}
                        </span>
                    </div>
                </div>
            )}

            {/* Statistics Cards */}
            {hasSearched && billingData.length > 0 && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                        {statsCards.map((card, index) => (
                            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                                <div className="flex items-center">
                                    <div className={`p-2 rounded-lg bg-${card.color}-50`}>
                                        <Clock className={`h-5 w-5 text-${card.color}-600`} />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-500">{card.title}</p>
                                        <p className="text-lg font-semibold text-gray-900">{card.value}</p>
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <p className="text-xs text-gray-500">{card.description}</p>
                                    <p className="text-xs text-gray-400">{card.count} records</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Last 24 Hours Processing Time Trend */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-2">
                            <Clock className="h-5 w-5 mr-2 text-indigo-600" />
                            File Processing Times (Last 24 Hours)
                            <span className="ml-3 text-sm font-medium text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full">
                                {last24HoursProcessingData.points.length} files
                            </span>
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Every processed file is shown as one point so spikes and outrageous times are easy to spot.
                        </p>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <ScatterChart
                                    margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        type="number"
                                        dataKey="timestamp"
                                        domain={['dataMin', 'dataMax']}
                                        tickFormatter={(value) => format(new Date(value), 'HH:mm')}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis
                                        type="number"
                                        dataKey="processingTime"
                                        allowDecimals={false}
                                        label={{ value: 'Seconds', angle: -90, position: 'insideLeft' }}
                                    />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (!active || !payload || !payload.length) return null;
                                            const point = payload[0]?.payload;
                                            if (!point) return null;

                                            return (
                                                <div
                                                    style={{
                                                        backgroundColor: 'white',
                                                        border: '1px solid #e5e7eb',
                                                        borderRadius: '0.375rem',
                                                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                                                        padding: '8px 10px'
                                                    }}
                                                >
                                                    <div style={{ fontSize: '12px', color: '#4b5563' }}>
                                                        {point.isOutlier ? 'Outlier' : 'Processed'} • {point.fullLabel}
                                                    </div>
                                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                                                        {point.processingTime}s • {point.pagesNo} pages
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                    <ReferenceLine
                                        y={last24HoursProcessingData.threshold}
                                        stroke="#f97316"
                                        strokeDasharray="4 4"
                                        label={{ value: 'Outlier threshold', position: 'insideTopRight', fill: '#f97316' }}
                                    />
                                    <Scatter
                                        data={last24HoursProcessingData.points}
                                        shape={(props) => {
                                            const { cx, cy, payload } = props;
                                            const color = payload?.isOutlier ? '#ef4444' : '#4f46e5';
                                            const radius = payload?.isOutlier ? 5 : 3;
                                            return <circle cx={cx} cy={cy} r={radius} fill={color} />;
                                        }}
                                    />
                                </ScatterChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </>
            )}

            {/* Billing Data Table */}
            {hasSearched && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <DollarSign className="h-5 w-5 mr-2 text-indigo-600" />
                            Billing Data
                            {billingData.length > 0 && (
                                <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({filteredData.length} records)
                                </span>
                            )}
                        </h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                            {/* Status Filter */}
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">Status:</span>
                                <div className="flex rounded-md shadow-sm">
                                    <button
                                        onClick={() => setStatusFilter('all')}
                                        className={`px-3 py-1 text-sm font-medium rounded-l-md ${statusFilter === 'all'
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        All
                                    </button>
                                    <button
                                        onClick={() => setStatusFilter('success')}
                                        className={`px-3 py-1 text-sm font-medium ${statusFilter === 'success'
                                            ? 'bg-green-600 text-white'
                                            : 'bg-white text-gray-700 border-t border-b border-r border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        Success
                                    </button>
                                    <button
                                        onClick={() => setStatusFilter('error')}
                                        className={`px-3 py-1 text-sm font-medium rounded-r-md ${statusFilter === 'error'
                                            ? 'bg-red-600 text-white'
                                            : 'bg-white text-gray-700 border-t border-b border-r border-gray-300 hover:bg-gray-50'
                                            }`}
                                    >
                                        Error
                                    </button>
                                </div>
                            </div>

                            {/* Items per page selector */}
                            {billingData.length > 0 && (
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm text-gray-500">Show:</span>
                                    <div className="relative">
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                                            className="text-sm border border-gray-300 rounded-md pl-2 pr-8 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white"
                                        >
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <ChevronLeft className="absolute right-2 top-1/2 transform -translate-y-1/2 rotate-90 h-3 w-3 text-gray-400 pointer-events-none" />
                                    </div>
                                    <span className="text-sm text-gray-500">per page</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            <span className="ml-3 text-gray-600">Loading billing data...</span>
                        </div>
                    ) : billingData.length === 0 ? (
                        <div className="text-center py-12">
                            <FileText className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No billing data found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                No billing records were found for this user.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ID
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Origin
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Pages
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Start Time
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            End Time
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <div className="flex items-center">
                                                <Clock className="h-4 w-4 mr-1" />
                                                Processing Time (s)
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {currentData.map((record, index) => (
                                        <tr key={record.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {record.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.isError ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                        Error
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Success
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.isError ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {record.origin}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.pagesNo}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.startTime ? formatDate(record.startTime) : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.timestamp ? formatDate(record.timestamp) : '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {record.processingTime !== null ? (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        {record.processingTime}s
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                        N/A
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Update pagination to use filtered data */}
                    {filteredData.length > 0 && totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200">
                            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
                                <div className="flex items-center text-sm text-gray-500">
                                    Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} results
                                </div>

                                <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
                                    {/* Custom Page Input */}
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-500">Go to page:</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value={customPageInput}
                                            onChange={(e) => setCustomPageInput(e.target.value)}
                                            onKeyPress={handleCustomPageKeyPress}
                                            placeholder="Page"
                                            className="w-16 text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        />
                                        <button
                                            onClick={handleCustomPageSubmit}
                                            className="text-sm px-2 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        >
                                            Go
                                        </button>
                                    </div>

                                    {/* Pagination Controls */}
                                    <div className="flex items-center space-x-1">
                                        {/* First Page */}
                                        <button
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                            className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="First page"
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                            <ChevronLeft className="h-4 w-4 -ml-1" />
                                        </button>

                                        {/* Previous Page */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft className="h-4 w-4 mr-1" />
                                            Previous
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="flex items-center space-x-1">
                                            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                                                let pageNumber;
                                                if (totalPages <= 5) {
                                                    pageNumber = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNumber = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNumber = totalPages - 4 + i;
                                                } else {
                                                    pageNumber = currentPage - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNumber}
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === pageNumber
                                                            ? 'bg-indigo-600 text-white'
                                                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {pageNumber}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Next Page */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                            <ChevronRight className="h-4 w-4 ml-1" />
                                        </button>

                                        {/* Last Page */}
                                        <button
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                            className="inline-flex items-center px-2 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Last page"
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                            <ChevronRight className="h-4 w-4 -ml-1" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Requests Graph */}
            {hasSearched && billingData.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <FileText className="h-5 w-5 mr-2 text-indigo-600" />
                            Processed Requests Over Time
                        </h3>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-4 sm:mt-0">
                            <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => handleDateChange('start', e.target.value)}
                                    max={endDate}
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            <span className="text-gray-500">to</span>
                            <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => handleDateChange('end', e.target.value)}
                                    min={startDate}
                                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={prepareGraphData()}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="time"
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                    interval="preserveStartEnd"
                                />
                                <YAxis allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '0.375rem',
                                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                                    }}
                                    formatter={(value, name, props) => {
                                        const { payload } = props;
                                        return [
                                            `${value} ${name === 'count' ? 'requests' : 'errors'}`,
                                            `${payload.date} ${payload.time}`
                                        ];
                                    }}
                                />
                                <Bar dataKey="count" fill="#4f46e5" radius={[4, 4, 0, 0]} name="Successful" />
                                <Bar dataKey="errorCount" fill="#ef4444" radius={[4, 4, 0, 0]} name="Errors" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
}
